<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import ReviewSummary from '../components/ReviewSummary.vue';
import { useFeedback } from '../composables/useFeedback';
import { useMarkReviewed, usePrefetchUpcomingReviews, useReview } from '../composables/useReviews';
import { errorDetail } from '../utils/error';
import type { ReviewType } from '../api';

const props = defineProps<{ reviewType: ReviewType; ids: string[] }>();

const router = useRouter();
const reviewType = computed(() => props.reviewType);
const index = ref(0);
const id = computed(() => props.ids[index.value] ?? '');

const { data: review, isPending, error } = useReview(reviewType, id);
usePrefetchUpcomingReviews(reviewType, () => props.ids, index);

const { notify } = useFeedback();
const markReviewed = useMarkReviewed(reviewType, id);

function next() {
  if (index.value + 1 < props.ids.length) {
    index.value += 1;
  } else {
    void router.push({ name: 'reviews', params: { reviewType: reviewType.value } });
  }
}

function approve() {
  if (!review.value || markReviewed.isPending.value) return;
  markReviewed.mutate(undefined, {
    onSuccess: () => {
      notify({ tone: 'success', summary: 'Marked as reviewed' });
      next();
    },
    onError: (err) => notify({ tone: 'error', summary: 'Failed to mark as reviewed', detail: errorDetail(err) }),
  });
}
</script>

<template>
  <div>
    <RouterLink :to="{ name: 'reviews', params: { reviewType } }">&laquo; Back</RouterLink>

    <p v-if="!ids.length" class="mt-4">No reviews selected.</p>
    <template v-else>
      <v-progress-linear
        :model-value="((index + 1) / ids.length) * 100"
        height="20"
        rounded
        color="primary"
        class="mt-4"
      >
        <strong class="text-caption">Review {{ index + 1 }} of {{ ids.length }}</strong>
      </v-progress-linear>

      <v-alert v-if="error" type="error" :text="errorDetail(error)" class="mt-4" />
      <v-progress-linear v-else-if="isPending" indeterminate class="mt-4" />
      <!-- Approving is what advances the stack, so an item the user may not review would
           otherwise strand them on it (only reachable via a hand-edited ids list); skip covers it. -->
      <ReviewSummary
        v-else-if="review"
        :review="review"
        :approving="markReviewed.isPending.value"
        skippable
        class="mt-4"
        @approve="approve"
        @skip="next"
      />
    </template>
  </div>
</template>
